/** Staff status of one PSV review item. A run's verification is complete only
 * when every item is Accepted or Concern; a note alone keeps it In review. */
export enum PsvReviewStatus {
  Pending = "pending",
  InReview = "in-review",
  Accepted = "accepted",
  Concern = "concern",
}

/** Statuses that finish an item. */
export const PSV_REVIEW_DECIDED_STATUSES: readonly PsvReviewStatus[] = [
  PsvReviewStatus.Accepted,
  PsvReviewStatus.Concern,
];

export enum PsvReviewItemKind {
  /** One state license, verified by any of its sources. */
  StateLicense = "state-license",
  /** One non-license source check, such as NPI or an exclusion list. */
  Check = "check",
}

/** What the automated sources concluded before staff decided. */
export enum PsvReviewOutcome {
  Valid = "valid",
  Issues = "issues",
  NotAvailable = "not-available",
}

export const PSV_REVIEW_NOTE_LIMIT = 4000;

/** How long a decision covers an unchanged screening issue on later runs:
 * one recredentialing cycle, so a provider's file never relies on a decision
 * older than that. */
export const PSV_ISSUE_DECISION_VALIDITY_MONTHS = 36;

/** The issue key of a Medicare opt-out affidavit: its 1-based position in its
 * sealed result, the same number the report shows as "Affidavit N", so the
 * report can label each affidavit. Positions are fixed once sealed. */
export function medicareAffidavitIssueKey(position: number): string {
  return `affidavit-${position}`;
}

/** The issue key of each screening candidate, in candidate order. Most
 * sources key candidates uniquely, but SAM Numbers repeat, so the second and
 * later occurrences get `#<occurrence>`. Sealed results never change, so the
 * same candidate always gets the same key. */
export function candidateIssueKeys(keys: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return keys.map((key) => {
    const count = (seen.get(key) ?? 0) + 1;
    seen.set(key, count);
    return count === 1 ? key : `${key}#${count}`;
  });
}

/** Where a provider's latest primary source verification stands. */
export enum PsvProviderStatus {
  NotRun = "not-run",
  InProgress = "in-progress",
  /** Collected; some items still need Accept or Concern. */
  NeedsReview = "needs-review",
  /** Every item decided, at least one as a concern. */
  Concern = "concern",
  /** Every item accepted. */
  Verified = "verified",
}
